import { toKebabCase } from "@utils/stringFormatters";

type ColorRecord = Record<string, any>;

const colorSlug = (color: ColorRecord): string =>
  toKebabCase(
    color?.colorSlug ||
      color?.color_slug ||
      color?.colorName ||
      color?.color_name ||
      color?.brand_color ||
      color?.name ||
      "",
  );

const sellableStatus = (
  color: ColorRecord | null | undefined,
): string | undefined => {
  const value = color?.sellableStatus ?? color?.sellable_status;
  return typeof value === "string" && value.trim()
    ? value.trim().toLowerCase()
    : undefined;
};

// A reference/on-road price is also returned for unavailable colours. Only a
// live sellable price can make a colour selectable or enable quote actions.
const hasPositiveTierPrice = (tier: ColorRecord | undefined | null) =>
  Boolean(tier) &&
  [
    "acko_drive_price",
    "final_price",
    "adsp_price",
  ].some((key) => Number(tier?.[key]) > 0);

const isSellableTier = (tier: ColorRecord | undefined | null): boolean => {
  if (!tier || typeof tier !== "object" || !Object.keys(tier).length) return false;
  const status = sellableStatus(tier);
  if (status) return status === "sellable";
  const dealType = String(tier.deal_type || tier.dealType || "").toLowerCase();
  if (dealType.includes("non_sellable") || dealType.includes("non sellable")) {
    return false;
  }
  if (typeof tier.is_available === "boolean" && tier.is_available) return true;
  if (typeof tier.available === "boolean" && tier.available) return true;
  return hasPositiveTierPrice(tier);
};

export const isMmvColorSellable = (color: ColorRecord | null | undefined) => {
  if (!color || typeof color !== "object") return false;
  const status = sellableStatus(color);
  if (status) return status === "sellable";
  return [
    color.expressPrice,
    color.express_price,
    color.expressInfo,
    color.express_info,
    color.adspPrice,
    color.adsp_price,
    color.standardInfo,
    color.standard_info,
    color.Adsp_details,
    color.adsp_details,
    color.Inventory_details,
    color.inventory_details,
  ].some(isSellableTier);
};

export const hasMmvColorPrice = (color: ColorRecord | null | undefined) => {
  const hasPrice = hasPositiveTierPrice;

  return Boolean(color) &&
    (hasPrice(color) ||
      [
        color?.expressPrice,
        color?.express_price,
        color?.adspPrice,
        color?.adsp_price,
        color?.standardInfo,
        color?.standard_info,
        color?.expressInfo,
        color?.express_info,
      ].some(hasPrice));
};

const hasSellabilitySignal = (color: ColorRecord | null | undefined) => {
  if (!color || typeof color !== "object") return false;
  const status = sellableStatus(color);
  if (status && status !== "unknown") return true;
  return [
    color.expressPrice,
    color.express_price,
    color.expressInfo,
    color.express_info,
    color.adspPrice,
    color.adsp_price,
    color.standardInfo,
    color.standard_info,
    color.Adsp_details,
    color.adsp_details,
    color.Inventory_details,
    color.inventory_details,
  ].some((tier) => {
    if (!tier || typeof tier !== "object") return false;
    const status = sellableStatus(tier);
    const dealType = String(tier.deal_type || tier.dealType || "").toLowerCase();

    // The product normaliser creates empty tier objects with `available: false`.
    // That is not a confirmed availability result; avoid turning a failed
    // pricing request into a false "no colours" state.
    return (
      Boolean(status && status !== "unknown") ||
      tier.is_available === true ||
      tier.available === true ||
      dealType.includes("non_sellable") ||
      dealType.includes("non sellable") ||
      hasPositiveTierPrice(tier)
    );
  });
};

export const getMmvPricingColor = (
  pricingByColor: Record<string, ColorRecord> | undefined,
  key: string,
) => {
  if (!pricingByColor || !key) return undefined;
  const matchedKey = Object.keys(pricingByColor).find(
    (candidate) => toKebabCase(candidate) === toKebabCase(key),
  );
  return matchedKey ? pricingByColor[matchedKey] : undefined;
};

export const resolveMmvCatalogSellability = ({
  catalogColors = [],
  pricingColors = [],
  pricingRequestSucceeded,
}: {
  catalogColors?: ColorRecord[];
  pricingColors?: ColorRecord[];
  pricingRequestSucceeded: boolean;
}) => {
  if (!catalogColors.length) {
    return {
      state: pricingRequestSucceeded ? ("resolved" as const) : ("unknown" as const),
      sellableColorKeys: new Set<string>(),
    };
  }
  const sellableColorKeys = new Set<string>();
  let unknown = false;

  catalogColors.forEach((catalogColor) => {
    const key = colorSlug(catalogColor);
    const pricingColor = pricingColors.find((color) => colorSlug(color) === key);
    if (sellableStatus(pricingColor) === "unknown") {
      unknown = true;
      return;
    }
    const availability =
      pricingColor && hasSellabilitySignal(pricingColor)
        ? pricingColor
        : hasSellabilitySignal(catalogColor)
          ? catalogColor
          : undefined;
    if (!availability) {
      unknown = true;
      return;
    }
    if (isMmvColorSellable(availability)) sellableColorKeys.add(key);
  });

  return {
    state:
      unknown || (!pricingRequestSucceeded && !catalogColors.every(hasSellabilitySignal))
        ? ("unknown" as const)
        : ("resolved" as const),
    sellableColorKeys,
  };
};

export const getMmvCatalogColorKey = colorSlug;
